import React from 'react';
import { DESKTOP_QUERY } from './PageBar';

/*
  A page that is on its way.

  **Built 10 September 2026 for job 25**, the last of Group H's thirteen system
  states, to the approved drawings `load-a` and `load-b`.

  **It was eighteen separate spinners.** Every one of them was written where it
  was needed, so they had drifted into four different paddings, two different
  sizes and two different wordings: four said "Loading..." underneath and
  fourteen said nothing. Two were not even the shared spinner. Nobody could see
  that from any one screen, which is exactly how OnHand ended up with six menus.

  **The word goes.** A spinner already says "wait", and writing "Loading..."
  underneath it is the app narrating itself, which is the same habit as the
  Coming Soon page explaining its own menu to the person reading it.

  **The shell stays**, rule 35, and this is the state where that is least
  arguable: the app is fine, the page is fine, it simply has not arrived.

  -- Two shapes, and the owner chose -----------------------------------------

  **He picked shape B on 10 September 2026.** Group H drew both and neither had
  ever been put to him.

  **Shape A is the spinner.** One line, anywhere, for ever, and it can never be
  wrong about what is coming because it does not claim to know. What it costs a
  person is that they wait at a blank page and find out what kind of page it is
  only when it arrives.

  **Shape B is the shape of what is coming**: grey bars where the rows will be.
  A person knows a list is on its way, roughly how long it is, and where to
  look, before it arrives. **On a slow connection at a shelf that is the
  difference between waiting and getting ready**, which is why he chose it.

  **What shape B costs, and it is the thing to watch.** It has to stay true. A
  shape that no longer matches the page it stands in for is a small lie every
  time that page opens, and it is the kind of lie nobody notices, because the
  real page arrives a moment later and covers it up.

  **So it is used where the thing arriving IS a list, and nowhere else.** Most
  of OnHand's loading places are lists, which is why one shape covers them
  rather than one per screen. **The three whole-screen loads keep the spinner**,
  because at that moment the dashboard has not worked out which page it is about
  to draw: a list of grey bars in front of a Home screen would be exactly the
  small lie above. `rows` is how a screen says what is coming, so one showing
  five rows does not promise twelve.

  -- The two arrangements -----------------------------------------------------

  **On a desk a row is a row**, five bars across, because the page arriving is a
  table with columns. **Below 1024 a row is a stack**, three bars down, because
  the page arriving is a card per row with the name over the details. Those are
  the two shapes OnHand's own lists take, so the outline matches whichever one
  is really coming.
*/

/* The columns of a row on a desk, and the widths are the drawing's. The first
   is darker because it is the one a person reads first: an item number, a
   name, a location. */
const DESK_COLUMNS = [
  { width: '16%', lead: true },
  { width: '30%' },
  { width: '12%' },
  { width: '22%' },
  { width: '9%' },
];

/* And below 1024, where a row is a card: the name, a line of detail, and a
   short third line. */
const HELD_LINES = [
  { width: '62%', lead: true },
  { width: '84%' },
  { width: '45%' },
];

export default function PageLoading({ tall = false, rows = 0 }) {
  const held = useHeld();

  /*
    **No number of rows means nothing knows what is coming, so it is the
    spinner.** That is not a fallback, it is the honest answer: a screen that
    cannot say what shape is arriving must not draw one.
  */
  /*
    **Rebrand step 3: no spinner at all** (guide 10.5, "skeleton shimmer,
    everywhere"). With no shape to promise, it is three plain lines in the
    same space the spinner took, which says "something is coming" without
    claiming what.
  */
  if (!rows) {
    return (
      <div className={'page-loading' + (tall ? ' tall' : '')} role="status" aria-label="Loading">
        <div className="page-loading-lines">
          <div className="skel-block" style={{ height: 12, width: '64%' }} />
          <div className="skel-block" style={{ height: 12, width: '88%' }} />
          <div className="skel-block" style={{ height: 12, width: '42%' }} />
        </div>
      </div>
    );
  }

  const bars = held ? HELD_LINES : DESK_COLUMNS;
  /* Capped, because an outline longer than the screen is a shape nobody sees
     the end of, and the drawing's own counts are seven on a desk and five
     below 1024. */
  const many = Math.max(1, Math.min(rows, held ? 5 : 8));

  return (
    <div className="skel" role="status" aria-label="Loading">
      <div className="skel-head" />
      {Array.from({ length: many }, (_, r) => (
        <div className={'skel-row' + (held ? ' stack' : '')} key={r}>
          {bars.map((b, i) => (
            <div className={'skel-bar' + (b.lead ? ' lead' : '')} style={{ width: b.width }} key={i} />
          ))}
        </div>
      ))}
    </div>
  );
}

/*
  Which arrangement to draw.

  **The app's one breakpoint**, the same query the shells split their trees on,
  so the outline and the list that replaces it are never in different shapes. A
  skeleton in the desk arrangement in front of a phone list would be the small
  lie this file warns about, at every width change.
*/
function useHeld() {
  const [held, setHeld] = React.useState(
    () => (typeof window !== 'undefined' && window.matchMedia
      ? !window.matchMedia(DESKTOP_QUERY).matches
      : false));
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(DESKTOP_QUERY);
    const sync = () => setHeld(!mq.matches);
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  return held;
}
